/**
 * API · preferencias de membresía de canal (Fase B·F2 · sin tabla nueva).
 *
 * Reusa columnas existentes de core.channel_members:
 *  - notification_pref text (default 'all') → frecuencia de notificaciones.
 *  - muted_until timestamptz → silenciar hasta una fecha (null = activo).
 * Y "salir del canal" = DELETE de la propia membresía.
 *
 * Policies existentes: members_update_self_or_admin (UPDATE self) y
 * members_delete_self_or_admin (DELETE self). Filtramos por user_id para
 * tocar SOLO la fila propia (un admin podría tocar otras, no queremos eso acá).
 */
import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type NotificationPref = 'all' | 'mentions' | 'none';

export const NOTIFICATION_PREF_LABEL: Record<NotificationPref, string> = {
  all: 'Todos los mensajes',
  mentions: 'Solo menciones',
  none: 'Nada',
};

export interface MyChannelPref {
  notification_pref: NotificationPref;
  muted_until: string | null;
}

export async function getMyChannelPref(
  channelId: string,
  userId: string,
): Promise<MyChannelPref | null> {
  const { data, error } = await db
    .from('channel_members')
    .select('notification_pref, muted_until')
    .eq('channel_id', channelId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    notification_pref: (data.notification_pref as NotificationPref) ?? 'all',
    muted_until: data.muted_until ?? null,
  };
}

export async function setNotificationPref(
  channelId: string,
  userId: string,
  pref: NotificationPref,
): Promise<void> {
  const { error } = await db
    .from('channel_members')
    .update({ notification_pref: pref })
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

/** muted_until ISO para silenciar hasta esa fecha, o null para reactivar. */
export async function setMuted(
  channelId: string,
  userId: string,
  mutedUntil: string | null,
): Promise<void> {
  const { error } = await db
    .from('channel_members')
    .update({ muted_until: mutedUntil })
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function leaveChannel(channelId: string, userId: string): Promise<void> {
  const { error } = await db
    .from('channel_members')
    .delete()
    .eq('channel_id', channelId)
    .eq('user_id', userId);
  if (error) throw error;
}

/** Helper: ISO "silenciado indefinidamente" (100 años) vs duración corta. */
export function mutedUntilFromPreset(preset: 'forever' | '1h' | '8h' | '1w'): string {
  const now = Date.now();
  const ms =
    preset === '1h'
      ? 3_600_000
      : preset === '8h'
        ? 8 * 3_600_000
        : preset === '1w'
          ? 7 * 24 * 3_600_000
          : 100 * 365 * 24 * 3_600_000; // forever
  return new Date(now + ms).toISOString();
}
